import { UpdateProjectContent } from 'api/graphQL/reactiveVars/workspaceContent'
import {
    ContentInsertVariables,
    InsertContentItemsFromHtmlResponse,
} from 'api/graphQL/types/contentInsert.types'
import { useProductFetcher } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher'
import { useTrackChangesQuery } from 'components/WorkspaceEditor/components/TrackChangesPanel/api/useTrackChangesQuery'
import { useNotification } from 'components/WorkspaceEditor/services'
import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { useCallback, useMemo } from 'react'
import { usePasteContentItemsFromHtmlMutation } from '../api/mutations/copyPasteContentMutations'
import { useCopyPasteShallowSelector } from '../store/CopyPasteStore'

const PASTE_UNSPECIFIED_ERROR_MESSAGE =
    'An unspecified error was encountered while pasting your content.'

interface UseHtmlPasteMutationHandlerProps {
    contentInsertVariables: Partial<ContentInsertVariables>
    setInstanceId: (instanceId: string) => void
    setWarningText: (warningText: string) => void
    setIsPasteInProgress: (isPasteInProgress: boolean) => void
    isWarningIgnored: boolean
}

export const useHtmlPasteMutationHandler = ({
    contentInsertVariables,
    setInstanceId,
    setWarningText,
    setIsPasteInProgress,
    isWarningIgnored,
}: UseHtmlPasteMutationHandlerProps) => {
    const notification = useNotification()

    const { activeTab, setIsTreeLoading, expandedKeys } = useShallowSelector(
        (state) => ({
            activeTab: state.activeTab,
            setIsTreeLoading: state.actions.setIsTreeLoading,
            expandedKeys: state.expandedKeys,
        })
    )

    const { resetCopyPasteState } = useCopyPasteShallowSelector((state) => ({
        resetCopyPasteState: state.resetCopyPasteState,
    }))

    const { setTrackChangesData } = useSuggestionsShallowSelector(
        ({ setTrackChangesData }) => ({
            setTrackChangesData,
        })
    )
    // Get expanded article IDs to fetch their content after paste
    const expandedArticleIds = useMemo(
        () => {
            const filteredKeys = (expandedKeys?.filter(
                (key) => typeof key === 'string'
            ) as string[]) || []
            return filteredKeys.length > 0 ? filteredKeys : null
        },
        [expandedKeys]
    )
    const { fetchTrackChangesData } = useTrackChangesQuery()

    const { getContentItemViewNodesWithApolloClient } = useProductFetcher({
        overrideContentItemNodes: !expandedArticleIds,
        onError: () => {
            setIsTreeLoading(false)
        },
    })

    const handlePasteCompleted = useCallback(
        async (result: Partial<UpdateProjectContent>) => {
            setIsPasteInProgress(false)
            resetCopyPasteState()
            setIsTreeLoading(true)

            // Fetch all content item view nodes after paste
            await getContentItemViewNodesWithApolloClient(
                expandedArticleIds,
                activeTab
            )

            // Clear existing track changes data before fetching new ones (for paste operations)
            // This forces the UI to update with fresh data from the server
            setTrackChangesData({}, [], true)

            // Fetch track changes data to update track change tips
            await fetchTrackChangesData()

            notification.success({
                message: 'Changes Saved Successfully',
            })
        },
        [
            notification,
            resetCopyPasteState,
            getContentItemViewNodesWithApolloClient,
            fetchTrackChangesData,
            setIsPasteInProgress,
            setIsTreeLoading,
            setTrackChangesData,
            activeTab,
            expandedArticleIds,
        ]
    )

    const handlePasteError = useCallback(
        (errorMessage: string | null | undefined) => {
            setIsTreeLoading(false)
            notification.error({
                message: errorMessage ?? PASTE_UNSPECIFIED_ERROR_MESSAGE,
            })
            resetCopyPasteState()
        },
        [notification, resetCopyPasteState, setIsTreeLoading]
    )

    const { pasteContentItemsFromHtml } = usePasteContentItemsFromHtmlMutation(
        (data: InsertContentItemsFromHtmlResponse) => {
            if (data.insertContentItemsFromHtml?.warnings?.length) {
                setWarningText(data.insertContentItemsFromHtml.warnings[0])
            } else if (data?.insertContentItemsFromHtml?.instanceId) {
                setInstanceId(data?.insertContentItemsFromHtml?.instanceId)
            } else {
                handlePasteError(
                    'Server returned an invalid response; cancelling paste.'
                )
            }
        },
        (error) => {
            handlePasteError(error.message)
        }
    )

    const executePasteMutation = useCallback(() => {
        if (contentInsertVariables) {
            setIsPasteInProgress(true)
            pasteContentItemsFromHtml({
                variables: {
                    input: {
                        ...contentInsertVariables.input!,
                        ignoreValidationWarnings: isWarningIgnored,
                    },
                    partType: contentInsertVariables.partType!,
                    productFamilyId: contentInsertVariables.productFamilyId!,
                    rootArticleContentItemIds: contentInsertVariables.rootArticleContentItemIds!,
                },
            })
        }
    }, [
        contentInsertVariables,
        isWarningIgnored,
        pasteContentItemsFromHtml,
        setIsPasteInProgress,
    ])

    return {
        handlePasteCompleted,
        handlePasteError,
        executePasteMutation,
    }
}
